import React, { useState, useEffect } from 'react';
import StatusChip from './StatusChip';
import { LENDER_STATUS_VARIANT } from '@constants';
import { LenderStatusEnum, UserStatusEnum } from '@types';
import { ReactComponent as PlusIcon } from '@assets/icons/plus_icon.svg';
import { ReactComponent as CheckIcon } from '@assets/icons/check_icon.svg';
import { LIST_LENDER_ORGANISATIONS, post, REQUEST_LENDER_BROKER, bulkRequestLenderBrokers } from '@services';
import { getCurrentUser } from '@utils/userUtils';
import clsx from 'clsx';
import ResultModal, { ResultMessage } from './ResultModal';
import ProspectLenderModal from './ProspectLenderModal';
import { useNavigate } from 'react-router-dom';

interface LenderListProps {
  className?: string;
}

interface LenderItem {
  isStub?: boolean;
  organisationId?: string;
  organisationName?: string;
  name?: string;
  status?: LenderStatusEnum | string;
  roles?: string[];
  [key: string]: any;
}

interface LenderBrokerItem {
  lenderOrganisationId?: string;
  lenderOrganisationName?: string;
  status?: LenderStatusEnum | string;
  isStub?: boolean;
}

const LenderList: React.FC<LenderListProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [lenderOrganisations, setLenderOrganisations] = useState<LenderItem[]>([]);
  const [prospectLenderOrganisations, setProspectLenderOrganisations] = useState<LenderItem[]>([]);
  const [lenderBrokers, setLenderBrokers] = useState<LenderBrokerItem[]>([]);
  const [openResultModal, setOpenResultModal] = useState(false);
  const [resultMessage, setResultMessage] = useState<ResultMessage | null>(null);
  const [prospectModalOpen, setProspectModalOpen] = useState(false);
  const [prospectModalInitialId, setProspectModalInitialId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const loadLenders = async () => {
      try {
        const currentUser = await getCurrentUser();
        setLenderBrokers(currentUser?.lenderBrokers || []);

        const lenderOrganisations = await post(LIST_LENDER_ORGANISATIONS, {});
        setLenderOrganisations(lenderOrganisations.lenderOrganisations.filter((organisation: LenderItem) => !organisation.isStub));
        setProspectLenderOrganisations(lenderOrganisations.lenderOrganisations.filter((organisation: LenderItem) => organisation.isStub));
      } catch (error) {
        console.error('Failed to load lenders:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLenders();
  }, []);

  if (isLoading) {
    return (
      <div className={`flex justify-center items-center py-8 ${className}`}>
        <div className="text-grey-600 text-sm">Loading lenders...</div>
      </div>
    );
  }

  const showResultMessage = (message: ResultMessage) => {
    setResultMessage(message);
    setOpenResultModal(true);
    setTimeout(() => {
      setOpenResultModal(false);
      setResultMessage(null);
    }, 4000);
  };

  const requestLenderBroker = async (lenderOrganisationId: string) => {
    const userId = localStorage.getItem('userId');
    await post(REQUEST_LENDER_BROKER, {
      lenderOrganisationId: lenderOrganisationId,
      userId: userId
    });

    const currentUser = await getCurrentUser();
    setLenderBrokers(currentUser?.lenderBrokers || []);
  };

  const handleRequestAccess = async (lenderOrganisationId: string) => {
    try {
      await requestLenderBroker(lenderOrganisationId);

      const lenderOrgs = await post(LIST_LENDER_ORGANISATIONS, {});
      setLenderOrganisations(
        lenderOrgs.lenderOrganisations.filter((organisation: LenderItem) => !organisation.isStub)
      );

      showResultMessage({ type: 'success', message: 'Request sent successfully' });
    } catch (err) {
      console.error('Failed to request access:', err);
      showResultMessage({ type: 'error', message: 'Failed to send request. Please try again.' });
    }
  };

  const handleExpressInterestMultiple = async (organisationIds: string[]) => {
    if (organisationIds.length === 0) return;
    try {
      await bulkRequestLenderBrokers(organisationIds);
      const currentUser = await getCurrentUser();
      setLenderBrokers(currentUser?.lenderBrokers || []);
    } catch (err) {
      console.error('Failed to request access:', err);
      showResultMessage({ type: 'error', message: 'Failed to send request. Please try again.' });
    }
  };

  const isProspectInterested = (organisationId?: string) => {
    if (!organisationId) return false;
    return lenderBrokers.some((broker) =>
      broker.lenderOrganisationId === organisationId &&
      broker.status === LenderStatusEnum.AccessRequested &&
      broker.isStub
    );
  };

  const onCloseResultModal = () => {
    setOpenResultModal(false);
    setResultMessage(null);
  };

  const getStatusLabel = (lender: LenderItem) => {
    const selectedBroker = lenderBrokers.filter((broker) => broker.lenderOrganisationId === lender.organisationId)?.[0];
    if (selectedBroker) {
      if (selectedBroker.status === LenderStatusEnum.AccessRequested) {
        return UserStatusEnum.ACCESS_REQUESTED_PENDING;
      }
      return UserStatusEnum[selectedBroker.status as keyof typeof UserStatusEnum];
    }
    return UserStatusEnum.NOT_REQUESTED;
  };

  const getStatus = (lender: LenderItem) => {
    const selectedBroker = lenderBrokers.filter((broker) => broker.lenderOrganisationId === lender.organisationId)?.[0];
    if (selectedBroker) {
      return selectedBroker.status as LenderStatusEnum;
    }
    return LenderStatusEnum.NotRequested;
  };

  const getActionButtonLabel = (lender: LenderItem) => {
    return getStatus(lender) === LenderStatusEnum.AccessRequested ? 'Access Requested' : 'Add lender';
  };

  const actionButtonDisabled = (lender: LenderItem) => {
    return [
      LenderStatusEnum.AccessRequested,
      LenderStatusEnum.Active,
      LenderStatusEnum.Pending,
      LenderStatusEnum.Declined,
    ].includes(getStatus(lender));
  };

  const statusChipElement = (status: LenderStatusEnum, statusVariant: any, statusLabel: string) => (
    status ? (
      <StatusChip variant={statusVariant} label={statusLabel} />
    ) : (
      <span className="text-xs text-grey-600 rounded-full bg-grey-50 border border-grey-200 px-2 py-0.5 whitespace-nowrap">
        Not requested
      </span>
    )
  );

  return (
    <div className={`py-6 ${className}`}>

      {/* Active lenders */}
      {lenderOrganisations.length === 0 ? (
        <div className="bg-white border border-grey-200 rounded-lg p-6 text-center mb-8">
          <p className="text-grey-600 text-sm">
            No lenders available. Request access to lenders to start creating inspections.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-grey-200 rounded-lg overflow-hidden mb-8">
          <div className="px-4 pt-4 pb-3">
            <h2 className="text-lg font-bold text-grey-900">Lenders</h2>
            <p className="text-sm text-grey-500 mt-0.5">
              Request access to lenders to create inspections. You'll need at least one active lender to get started.
            </p>
          </div>

          {/* Column headers — desktop only */}
          <div className="hidden sm:flex border-t border-b border-grey-200 bg-grey-50">
            <div className="flex-[0.5] text-left text-xs font-medium tracking-wide uppercase text-grey-500 py-3 px-4">Lender</div>
            <div className="flex-[0.25] text-center text-xs font-medium tracking-wide uppercase text-grey-500 py-3 px-4">Status</div>
            <div className="flex-[0.25] text-center text-xs font-medium tracking-wide uppercase text-grey-500 py-3 px-4">Action</div>
          </div>

          <div className="border-t border-grey-200 sm:border-t-0">
            {lenderOrganisations.map((lender, index) => {
              const lenderName = lender.organisationName || lender.name || 'Unknown Lender';
              const status = getStatus(lender);
              const statusVariant = status && LENDER_STATUS_VARIANT[status] ? LENDER_STATUS_VARIANT[status] : 'text';
              const statusLabel = getStatusLabel(lender);
              const isCurrentLenderActive = status === LenderStatusEnum.Active;

              const actionButton = isCurrentLenderActive ? (
                <button
                  onClick={() => navigate('/inspections/create', { state: { preselectedLenderOrganisationId: lender.organisationId } })}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white hover:opacity-90 rounded-md transition-opacity">
                  <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 bg-white/20">
                    <PlusIcon className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-sm font-medium">Create inspection</span>
                </button>
              ) : (
                <button
                  disabled={actionButtonDisabled(lender)}
                  onClick={() => handleRequestAccess(lender.organisationId)}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-1.5 border border-grey-200 rounded-md transition-colors bg-white',
                    actionButtonDisabled(lender) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-grey-50'
                  )}>
                  {!actionButtonDisabled(lender) && (
                    <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 bg-grey-100">
                      <PlusIcon className="w-3 h-3 text-grey-600" />
                    </div>
                  )}
                  <span className="text-sm font-medium text-grey-700">{getActionButtonLabel(lender)}</span>
                </button>
              );

              return (
                <div key={lender.organisationId || index} className="flex flex-col sm:flex-row border-b border-grey-200 last:border-b-0 hover:bg-grey-50 transition-colors">
                  {/* Mobile: name row with status chip inline */}
                  <div className="flex-[0.5] pt-4 pb-2 sm:pb-4 px-4 flex items-start justify-between sm:block">
                    <span className="text-base text-grey-900 font-medium">{lenderName}</span>
                    <div className="sm:hidden ml-3 flex-shrink-0">
                      {statusChipElement(status, statusVariant, statusLabel)}
                    </div>
                  </div>
                  {/* Desktop-only status column */}
                  <div className="hidden sm:flex flex-[0.25] py-5 px-4 justify-center items-center">
                    {statusChipElement(status, statusVariant, statusLabel)}
                  </div>
                  {/* Action */}
                  <div className="flex-[0.25] pb-4 sm:py-4 px-4 flex sm:justify-center">
                    {actionButton}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Prospect lenders — coming soon chip grid */}
      {prospectLenderOrganisations.length > 0 && (
        <div className="bg-white border border-grey-200 rounded-lg overflow-hidden">
          <div className="px-4 pt-4 pb-3">
            <h2 className="text-lg font-bold text-grey-900">More lenders coming soon</h2>
            <p className="text-sm text-grey-500 mt-0.5">
              We are actively working to bring these lenders onto Authentik8. Click <span className="font-semibold text-grey-700">+</span> on any lender to register your interest.
            </p>
          </div>

          <div className="px-4 pb-4 flex flex-wrap gap-2">
            {prospectLenderOrganisations.map((prospect) => {
              const prospectName = prospect.organisationName || prospect.name || 'Unknown Lender';
              const isInterested = isProspectInterested(prospect.organisationId);

              if (isInterested) {
                return (
                  <span
                    key={prospect.organisationId}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-success-50 text-success-700 border border-success-800">

                    <CheckIcon className="w-3 h-3" />
                    {prospectName}
                  </span>
                );
              }

              return (
                <button
                  key={prospect.organisationId}
                  onClick={() => {
                    setProspectModalInitialId(prospect.organisationId);
                    setProspectModalOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full border border-grey-300 bg-white text-grey-700 hover:border-secondary hover:text-secondary hover:bg-blue-50 transition-colors">
                  <span className="font-bold text-grey-400 leading-none">+</span>
                  {prospectName}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <ProspectLenderModal
        open={prospectModalOpen}
        prospects={prospectLenderOrganisations
          .filter((p) => !isProspectInterested(p.organisationId))
          .map((p) => ({
            organisationId: p.organisationId!,
            name: p.organisationName || p.name || 'Unknown Lender',
          }))}
        initialSelectedId={prospectModalInitialId}
        onClose={() => setProspectModalOpen(false)}
        onSubmit={handleExpressInterestMultiple}
      />

      <ResultModal
        open={openResultModal}
        resultMessage={resultMessage}
        onClose={onCloseResultModal}
      />
    </div>
  );
};

export default LenderList;
